import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from './cn';

export type ToastTone = 'info' | 'success' | 'error' | 'warn';

export interface ToastOptions {
  tone?: ToastTone;
  /** Auto-dismiss after this many ms (0 = stay until closed). */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: ReactNode;
  tone: ToastTone;
  durationMs: number;
}

interface ToastApi {
  show: (message: ReactNode, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONES: Record<ToastTone, string> = {
  info: 'border-line',
  success: 'border-correct',
  error: 'border-wrong',
  warn: 'border-warn',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: ReactNode, options: ToastOptions = {}) => {
    const id = nextId.current++;
    const item: ToastItem = {
      id,
      message,
      tone: options.tone ?? 'info',
      durationMs: options.durationMs ?? 4000,
    };
    setItems((current) => [...current.slice(-3), item]);
    return id;
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
        aria-live="polite"
        role="region"
        aria-label="Oznámení"
      >
        {items.map((item) => (
          <ToastView key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (!item.durationMs) return;
    const timer = setTimeout(() => onDismiss(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item, onDismiss]);

  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-full max-w-md animate-slide-up items-start gap-3 rounded-xl',
        'border-l-4 bg-elevated px-4 py-3 text-fg shadow-lg',
        TONES[item.tone],
      )}
    >
      <div className="flex-1 text-sm">{item.message}</div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="-m-1 rounded p-1 text-muted hover:text-fg"
        aria-label="Zavřít oznámení"
      >
        ✕
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
